import { bakeRamp, rampColor } from '../../core/color';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { DEG, clamp01, TAU } from '../../core/math';
import { buildGlyphPath, drawableGlyphs, scaleForHeight } from '../../core/text/glyphDraw';
import type { GlyphInstance } from '../../core/text/layout';
import type { RenderTarget, TechniqueContext } from '../types';
import type { LSystemTreeValues } from './params';

/**
 * Recursive branching structure whose tips carry the actual glyphs as leaves.
 *
 * This is the counterpart to the field-based techniques: instead of reading the
 * distance field, it uses the per-glyph decomposition, drawing real letter outlines
 * at the branch tips. It is the reason `TextField` exposes glyphs individually.
 *
 * Branches are stored flat rather than as a tree of objects — the drawing order is
 * depth-first anyway, and a 12-level binary tree is 4 095 nodes that would otherwise
 * be 4 095 allocations on every parameter change.
 */
export interface Branch {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  angle: number;
  depth: number;
  thickness: number;
  /** True when nothing grows further from this branch — a candidate for a leaf. */
  isTip: boolean;
}

export interface Leaf {
  x: number;
  y: number;
  rotation: number;
  size: number;
  /** Index into the drawable glyph list, or -1 for the whole word. */
  glyph: number;
  tint: number;
  fallen: boolean;
}

export interface LSystemTreeState {
  branches: Branch[];
  leaves: Leaf[];
  /** Depth-first generation frontier, so the tree can be built across frames. */
  frontier: Branch[];
  currentDepth: number;
  /** Animation progress in seconds since the state was created. */
  elapsed: number;
  built: boolean;
  /** Seconds the reveal animation runs for; 0 when growth is disabled. */
  growDuration: number;
  branchRamp: Uint8Array;
  leafPalette: ResolvedPalette;
  glyphs: GlyphInstance[];
}

/**
 * Hard ceiling on the branch count.
 *
 * The structure is exponential: depth 12 with five children per node is 244 million
 * branches, which would exhaust memory long before it finished drawing. The cap turns
 * an unusable parameter combination into a dense but finite tree, and the remaining
 * frontier simply becomes tips — so it still grows leaves and still looks deliberate.
 */
const MAX_BRANCHES = 60000;

function values(ctx: TechniqueContext): LSystemTreeValues {
  return ctx.values as unknown as LSystemTreeValues;
}

export function init(ctx: TechniqueContext): LSystemTreeState {
  const v = values(ctx);
  const glyphs = drawableGlyphs(ctx.field.glyphs);

  const root: Branch = {
    x0: v.root.x * ctx.width,
    y0: v.root.y * ctx.height,
    x1: v.root.x * ctx.width,
    y1: v.root.y * ctx.height - v.trunkLength * ctx.height,
    angle: -Math.PI / 2,
    depth: 0,
    thickness: v.thickness,
    isTip: false,
  };

  return {
    branches: [root],
    leaves: [],
    frontier: [root],
    currentDepth: 0,
    elapsed: 0,
    built: false,
    growDuration: v.grow ? v.growSpeed : 0,
    branchRamp: bakeRamp(v.trunkColor, v.branchTipColor, 32),
    leafPalette: resolvePalette(v.leafPalette, ctx.rng.fork(2)),
    glyphs,
  };
}

export function step(state: LSystemTreeState, dt: number, ctx: TechniqueContext): LSystemTreeState {
  state.elapsed += dt;
  if (state.built) return state;

  const v = values(ctx);
  const { rng, field } = ctx;

  // One depth level per step: the structure appears progressively, and the frame cost
  // stays bounded even at depth 12 with five children per node.
  const next: Branch[] = [];
  for (const parent of state.frontier) {
    const childDepth = parent.depth + 1;
    if (childDepth > v.depth || state.branches.length >= MAX_BRANCHES) {
      parent.isTip = true;
      continue;
    }

    const length = Math.hypot(parent.x1 - parent.x0, parent.y1 - parent.y0) * v.lengthRatio;
    const count = v.children;
    let grew = false;

    for (let i = 0; i < count; i++) {
      // Spread the children symmetrically around the parent direction.
      const spread = count === 1 ? 0 : (i / (count - 1)) * 2 - 1;
      const jitter = rng.signed(v.angleJitter * DEG);
      let angle = parent.angle + spread * v.angle * DEG + jitter;

      // Gravity bends every branch towards straight up (negative) or straight down
      // (positive), which is what separates a poplar from a willow.
      const target = v.gravity < 0 ? -Math.PI / 2 : Math.PI / 2;
      const delta = ((target - angle + Math.PI * 3) % TAU) - Math.PI;
      angle += delta * Math.abs(v.gravity) * 0.35;

      const scale = 1 - rng.next() * v.asymmetry;
      const len = length * scale;
      const x1 = parent.x1 + Math.cos(angle) * len;
      const y1 = parent.y1 + Math.sin(angle) * len;

      // A blocked child must not take its siblings with it — the parent only becomes a
      // tip if *none* of its children found room.
      if (v.avoidText && field.isInside(x1, y1)) continue;

      const branch: Branch = {
        x0: parent.x1,
        y0: parent.y1,
        x1,
        y1,
        angle,
        depth: childDepth,
        thickness: Math.max(0.25, parent.thickness * v.thicknessFalloff),
        isTip: childDepth === v.depth,
      };
      state.branches.push(branch);
      grew = true;
      if (childDepth < v.depth) next.push(branch);
    }

    parent.isTip = !grew;
  }

  state.frontier = next;
  state.currentDepth += 1;

  if (next.length === 0 || state.currentDepth >= v.depth || state.branches.length >= MAX_BRANCHES) {
    state.frontier = [];
    state.built = true;
    buildLeaves(state, ctx, v);
  }

  return state;
}

function buildLeaves(state: LSystemTreeState, ctx: TechniqueContext, v: LSystemTreeValues): void {
  const { rng } = ctx;
  const glyphCount = state.glyphs.length;
  const ground = v.groundLevel * ctx.height;

  let order = 0;
  for (const branch of state.branches) {
    if (!branch.isTip) continue;
    if (!rng.chance(v.leafChance)) continue;

    const fallen = rng.chance(v.fallenRatio);
    const sizeJitter = 1 + rng.signed(v.leafSizeJitter);
    const size = Math.max(2, v.leafSize * sizeJitter);

    let rotation: number;
    if (v.leafRotation === 'branch') rotation = branch.angle + Math.PI / 2;
    else if (v.leafRotation === 'random') rotation = rng.next() * TAU;
    else rotation = 0;
    rotation += rng.signed(v.leafRotationJitter * DEG);

    // Fallen leaves lie flat near the ground, scattered under the canopy.
    const x = fallen ? branch.x1 + rng.signed(size * 2.2) : branch.x1;
    const y = fallen ? ground - rng.next() * size * 0.5 : branch.y1;
    if (fallen && v.leafRotation !== 'random') rotation = rng.signed(0.45);

    state.leaves.push({
      x,
      y,
      rotation,
      size,
      glyph: v.leafContent === 'word' ? -1 : glyphCount > 0 ? order % glyphCount : -1,
      tint: rng.next(),
      fallen,
    });
    order++;
  }
}

export function isComplete(state: LSystemTreeState): boolean {
  // The structure being finished is not enough — the reveal animation still has to run
  // out, or the render loop would idle mid-growth and freeze a half-drawn tree.
  return state.built && state.elapsed >= state.growDuration;
}

export function draw(state: LSystemTreeState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const maxDepth = Math.max(1, v.depth);

  // Growth reveal: branches extend by depth, leaves appear once everything is out.
  const progress = state.growDuration > 0 ? clamp01(state.elapsed / state.growDuration) : 1;
  const revealedDepth = progress * (maxDepth + 1);

  c.lineCap = 'round';
  for (const branch of state.branches) {
    const reveal = clamp01(revealedDepth - branch.depth);
    if (reveal <= 0) continue;

    c.strokeStyle = rampColor(state.branchRamp, branch.depth / maxDepth, 1);
    c.lineWidth = branch.thickness;
    c.beginPath();
    c.moveTo(branch.x0, branch.y0);
    // Partially grown branches extend from their base, so growth reads as growth
    // rather than as a fade.
    c.lineTo(branch.x0 + (branch.x1 - branch.x0) * reveal, branch.y0 + (branch.y1 - branch.y0) * reveal);
    c.stroke();
  }

  if (progress < 1) return;

  const glyphs = state.glyphs;
  if (glyphs.length === 0) return;

  for (const leaf of state.leaves) {
    c.fillStyle = state.leafPalette.at(leaf.tint, v.leafOpacity);

    if (leaf.glyph >= 0) {
      const glyph = glyphs[leaf.glyph];
      const scale = scaleForHeight(glyph, leaf.size);
      c.fill(buildGlyphPath(glyph, leaf.x, leaf.y, scale, leaf.rotation), 'nonzero');
      continue;
    }

    // Whole-word leaves: scale the entire block to the leaf size and lay it out around
    // the tip, keeping the original relative glyph positions.
    const bounds = ctx.field.bounds;
    if (bounds.height <= 0.01) continue;
    const scale = leaf.size / bounds.height;
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    const cos = Math.cos(leaf.rotation);
    const sin = Math.sin(leaf.rotation);
    const path = new Path2D();
    for (const glyph of glyphs) {
      for (const contour of glyph.contours) {
        const p = contour.points;
        for (let i = 0; i < p.length; i += 2) {
          const lx = (p[i] - cx) * scale;
          const ly = (p[i + 1] - cy) * scale;
          const px = leaf.x + lx * cos - ly * sin;
          const py = leaf.y + lx * sin + ly * cos;
          if (i === 0) path.moveTo(px, py);
          else path.lineTo(px, py);
        }
        path.closePath();
      }
    }
    c.fill(path, 'nonzero');
  }
}

export function elementCount(state: LSystemTreeState): number {
  return state.branches.length + state.leaves.length;
}

/**
 * Vector output: branches as stroked lines, leaves as real glyph outlines.
 *
 * The growth animation is deliberately ignored — an export is a finished picture, not
 * a frame of one, so the tree is always emitted fully grown.
 */
export function toSVG(state: LSystemTreeState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const maxDepth = Math.max(1, v.depth);
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');
  const parts: string[] = ['<g stroke-linecap="round">'];

  for (const b of state.branches) {
    parts.push(
      `<line x1="${f(b.x0)}" y1="${f(b.y0)}" x2="${f(b.x1)}" y2="${f(b.y1)}" ` +
        `stroke="${rampColor(state.branchRamp, b.depth / maxDepth, 1)}" stroke-width="${f(b.thickness)}"/>`,
    );
  }
  parts.push('</g>');

  const glyphs = state.glyphs;
  if (glyphs.length > 0) {
    parts.push(`<g fill-opacity="${v.leafOpacity}">`);
    for (const leaf of state.leaves) {
      const color = state.leafPalette.at(leaf.tint);
      if (leaf.glyph >= 0) {
        const glyph = glyphs[leaf.glyph];
        const s = scaleForHeight(glyph, leaf.size);
        parts.push(`<path fill="${color}" fill-rule="nonzero" d="${glyphPathData(glyph.contours, glyph, leaf, s, f)}"/>`);
      } else {
        const bounds = ctx.field.bounds;
        if (bounds.height <= 0.01) continue;
        const s = leaf.size / bounds.height;
        const cx = bounds.x + bounds.width / 2;
        const cy = bounds.y + bounds.height / 2;
        const cos = Math.cos(leaf.rotation);
        const sin = Math.sin(leaf.rotation);
        let d = '';
        for (const glyph of glyphs) {
          for (const contour of glyph.contours) {
            const p = contour.points;
            for (let i = 0; i < p.length; i += 2) {
              const lx = (p[i] - cx) * s;
              const ly = (p[i + 1] - cy) * s;
              d += `${i === 0 ? 'M' : 'L'}${f(leaf.x + lx * cos - ly * sin)} ${f(leaf.y + lx * sin + ly * cos)}`;
            }
            d += 'Z';
          }
        }
        parts.push(`<path fill="${color}" fill-rule="nonzero" d="${d}"/>`);
      }
    }
    parts.push('</g>');
  }

  return parts.join('\n');
}

function glyphPathData(
  contours: GlyphInstance['contours'],
  glyph: GlyphInstance,
  leaf: Leaf,
  scale: number,
  f: (n: number) => string,
): string {
  const cx = glyph.bounds.x + glyph.bounds.width / 2;
  const cy = glyph.bounds.y + glyph.bounds.height / 2;
  const cos = Math.cos(leaf.rotation);
  const sin = Math.sin(leaf.rotation);
  let d = '';
  for (const contour of contours) {
    const p = contour.points;
    for (let i = 0; i < p.length; i += 2) {
      const lx = (p[i] - cx) * scale;
      const ly = (p[i + 1] - cy) * scale;
      d += `${i === 0 ? 'M' : 'L'}${f(leaf.x + lx * cos - ly * sin)} ${f(leaf.y + lx * sin + ly * cos)}`;
    }
    d += 'Z';
  }
  return d;
}
